/**
 * PATCH `public.quotes` via PostgREST + JWT from localStorage (bypasses supabase-js `getSession()` stalls).
 */
import { readSupabaseAuthAccessTokenFromLocalStorage } from './supabaseProfilesDiag';

const PATCH_TIMEOUT_MS = 25000;

/**
 * @param {string} quoteId
 * @param {Record<string, unknown>} partialRow Column(s) to PATCH. Adds `updated_at` if missing.
 */
export async function patchQuoteColumns(quoteId, partialRow) {
  const supabaseUrl = (process.env.REACT_APP_SUPABASE_URL || '').trim();
  const anonKey = (process.env.REACT_APP_SUPABASE_ANON_KEY || '').trim();
  if (!supabaseUrl || !anonKey || !quoteId) {
    throw new Error('Missing Supabase configuration or quote.');
  }
  const token = readSupabaseAuthAccessTokenFromLocalStorage(supabaseUrl);
  if (!token) {
    throw new Error('Your session was not found. Please sign in again.');
  }

  const row = { ...partialRow };
  if (!('updated_at' in row)) {
    row.updated_at = new Date().toISOString();
  }

  const base = supabaseUrl.replace(/\/$/, '');
  const url = `${base}/rest/v1/quotes?id=eq.${encodeURIComponent(quoteId)}`;
  const body = JSON.stringify(row);

  const ac = new AbortController();
  const tid = setTimeout(() => ac.abort(), PATCH_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      method: 'PATCH',
      headers: {
        apikey: anonKey,
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Prefer: 'return=minimal',
      },
      body,
      signal: ac.signal,
    });
    if (res.ok) return;

    const text = await res.text();
    let msg = `Could not update quote (${res.status})`;
    try {
      const j = JSON.parse(text);
      if (j && typeof j.message === 'string') msg = j.message;
    } catch {
      if (text) msg = text.slice(0, 240);
    }
    const err = new Error(msg);
    /** @type {{ status?: number }} */ (err).status = res.status;
    throw err;
  } finally {
    clearTimeout(tid);
  }
}

/**
 * @param {string} quoteId
 * @param {'paid' | 'awaiting'} invoicePaymentStatus
 */
export async function patchQuoteInvoicePaymentStatus(quoteId, invoicePaymentStatus) {
  return patchQuoteColumns(quoteId, {
    invoice_payment_status: invoicePaymentStatus,
  });
}
